import {
  Box,
  Button,
  Input,
  NativeSelect,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  Edit,
  SimpleForm,
  Labeled,
  ReferenceManyField,
  useEditContext,
  useUpdate,
  useNotify,
  useRecordContext,
  SelectInput,
  Pagination,
} from 'react-admin';
import { USER_ROLES_LIST } from '../../constants';
import { EventsListDatagrid } from '../Event';

const BAN_DURATION_OPTIONS = ['forever', '20m', '2h', '1d', '1y'];

export const UserEdit = () => {
  return (
    <Edit title={<Title />}>
      <SimpleForm toolbar={false}>
        <UserInfo />
        <Typography marginTop={4} variant="h5">
          Events:
        </Typography>
        <ReferenceManyField
          pagination={<Pagination />}
          reference="/events"
          target="userCreatedId"
        >
          <EventsListDatagrid />
        </ReferenceManyField>
      </SimpleForm>
    </Edit>
  );
};

const Title = () => {
  const record = useRecordContext();
  return (
    <span>User {record ? `"${record.firstName} ${record.lastName}"` : ''}</span>
  );
};

const UserInfo = () => {
  const { record, isLoading, refetch } = useEditContext();
  const [update] = useUpdate();
  const [banReason, setBanReason] = useState<string>(record.banReason ?? '');
  const [role, setRole] = useState<string>(record.role ?? '');

  const [banDuration, setBanDuration] = useState<string>(
    BAN_DURATION_OPTIONS[0],
  );
  const notify = useNotify();

  if (isLoading) return null;

  const handleActionClick = async () => {
    await update(
      '/users',
      {
        id: record.id,
        data: {
          method: record.isBanned ? 'unban' : 'ban',
          role: role,
          reason: banReason,
          duration: banDuration,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          refetch?.();
          notify('Successfully updated the user', { type: 'success' });
        },
        onError: () => {
          notify(`Error happened during the record update`, { type: 'error' });
        },
      },
    );
  };
  const updateRole = async () => {
    await update(
      '/users',
      {
        id: record.id,
        data: {
          role: role,
          method: 'set-role',
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          refetch?.();
          notify('Successfully updated the user', { type: 'success' });
        },
        onError: () => {
          notify(`Error happened during the record update`, { type: 'error' });
        },
      },
    );
  };

  return (
    <Box minWidth="200px" mb={2}>
      <Stack gap={1}>
        <Typography variant="h5">{record.name}</Typography>
        <Labeled label="Nickname">
          <Typography variant="body1">{record.nickname}</Typography>
        </Labeled>

        <Stack gap={1} flexDirection="row" alignItems="flex-start">
          <SelectInput
            source="role"
            choices={USER_ROLES_LIST.map((role) => ({ id: role, name: role }))}
            value={role}
            onChange={(event) => setRole(event.target.value)}
          ></SelectInput>
          <Button
            fullWidth
            variant="contained"
            onClick={updateRole}
            disabled={role === record.role}
            size="large"
            sx={{ marginTop: '10px' }}
          >
            {'Save'}
          </Button>
        </Stack>

        <Labeled label="Is banned">
          <Typography variant="body1">
            {record.isBanned ? 'yes' : 'no'}
          </Typography>
        </Labeled>

        {record.banReason && (
          <Labeled label="Ban reason">
            <Typography variant="body1">{record.banReason}</Typography>
          </Labeled>
        )}
        {record.dateBannedTo && (
          <Labeled label="Date banned to">
            <Typography variant="body1">{record.dateBannedTo}</Typography>
          </Labeled>
        )}
      </Stack>

      <Stack mt={2} gap={1}>
        {!record.isBanned && (
          <>
            <Labeled label="Ban reason">
              <Input
                value={banReason}
                onChange={(event) => setBanReason(event.target.value)}
              ></Input>
            </Labeled>

            <Labeled label="Ban duration">
              <NativeSelect
                value={banDuration}
                onChange={(event) => setBanDuration(event.target.value)}
              >
                {BAN_DURATION_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </NativeSelect>
            </Labeled>
          </>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleActionClick}
          color={record.isBanned ? 'success' : 'error'}
          disabled={record.isBanned ? false : !(banReason && banDuration)}
        >
          {record.isBanned ? 'Unban' : 'Ban'}
        </Button>
      </Stack>
    </Box>
  );
};
